// Vuex module for the /integrations page: connection-status card grid for all
// integrations (xero / stripe / hubspot / mailgun / pedalme), test-connection,
// and the Xero connect/disconnect/status flow.
import { EVENTS, track, enumValue } from "~/utils/analytics"
//
// Contacts (Organisation/Maker) sync to Xero automatically from the backend —
// there are no per-entity push buttons here. The maker purchase-bill flow lives
// in the separate `xero` store module.

function authConfig() {
	return {
		headers: {
			accept: "application/json",
			"x-access-token-user": useCookie("auth_token").value ?? "",
			"content-type": "application/json",
		},
	}
}

const integrationsStoreModule = {
	namespaced: true,
	state: () => ({
		integrations: [],
		integrationsLoading: false,
		integrationsError: null,

		// Xero-specific connection summary
		xeroStatus: null,
		xeroStatusLoading: false,
		xeroConnectLoading: false,
		xeroDisconnectLoading: false,
	}),
	mutations: {
		SET_INTEGRATIONS(state, payload) {
			state.integrations = payload
		},
		SET_INTEGRATIONS_LOADING(state, payload) {
			state.integrationsLoading = payload
		},
		SET_INTEGRATIONS_ERROR(state, payload) {
			state.integrationsError = payload
		},
		SET_XERO_STATUS(state, payload) {
			state.xeroStatus = payload
		},
		SET_XERO_STATUS_LOADING(state, payload) {
			state.xeroStatusLoading = payload
		},
		SET_XERO_CONNECT_LOADING(state, payload) {
			state.xeroConnectLoading = payload
		},
		SET_XERO_DISCONNECT_LOADING(state, payload) {
			state.xeroDisconnectLoading = payload
		},
	},
	actions: {
		async fetchIntegrations({ commit, dispatch }) {
			commit("SET_INTEGRATIONS_LOADING", true)
			commit("SET_INTEGRATIONS_ERROR", null)
			try {
				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/integrations`,
					authConfig()
				)
				// Backend returns the array directly OR { integrations: [...] }.
				const list = Array.isArray(response) ? response : ((response as any)?.integrations ?? [])
				commit("SET_INTEGRATIONS", list)
			} catch (error: any) {
				commit("SET_INTEGRATIONS_ERROR", error?.message ?? "Failed to load integrations")
				dispatch("handleError", { error }, { root: true })
			} finally {
				commit("SET_INTEGRATIONS_LOADING", false)
			}
		},

		async testConnection({ dispatch }, { type }) {
			try {
				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/integrations/${type}/test`,
					authConfig()
				)
				track(EVENTS.INTEGRATION_CONNECTION_CHANGED, {
					integration: enumValue(type),
					change_type: "test_run",
					test_result: (response as any)?.ok === false ? "failed" : "ok",
				})
				return response
			} catch (error: any) {
				track(EVENTS.INTEGRATION_CONNECTION_CHANGED, {
					integration: enumValue(type),
					change_type: "test_run",
					test_result: "failed",
				})
				dispatch("handleError", { error }, { root: true })
				return { ok: false, message: error?.message ?? "Test failed" }
			}
		},

		// ── Xero connect flow ───────────────────────────────────────────────

		async fetchXeroStatus({ commit, dispatch }) {
			commit("SET_XERO_STATUS_LOADING", true)
			try {
				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/xero/status`,
					authConfig()
				)
				commit("SET_XERO_STATUS", response)
			} catch (error) {
				dispatch("handleError", { error }, { root: true })
			} finally {
				commit("SET_XERO_STATUS_LOADING", false)
			}
		},

		// Starts the OAuth dance. Backend returns the consent URL; we navigate
		// the browser to it. Xero redirects back to /callback which redirects
		// to /integrations?xero=connected|failed.
		async connectXero({ commit, dispatch }) {
			commit("SET_XERO_CONNECT_LOADING", true)
			try {
				const response: { url: string } = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/xero/auth`,
					authConfig()
				)
				if (response?.url) {
					// sendBeacon: the navigation below would kill a normal XHR. A
					// connect_started with no later /integrations?xero=connected page
					// view means the OAuth dance is failing.
					track(
						EVENTS.INTEGRATION_CONNECTION_CHANGED,
						{ integration: "xero", change_type: "connect_started" },
						{ beacon: true }
					)
					window.location.href = response.url
					return
				}
				throw new Error("No Xero auth URL returned")
			} catch (error) {
				dispatch("handleError", { error }, { root: true })
			} finally {
				commit("SET_XERO_CONNECT_LOADING", false)
			}
		},

		async disconnectXero({ commit, dispatch }) {
			commit("SET_XERO_DISCONNECT_LOADING", true)
			try {
				await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/xero/disconnect`, {
					method: "POST",
					...authConfig(),
				})
				track(EVENTS.INTEGRATION_CONNECTION_CHANGED, {
					integration: "xero",
					change_type: "disconnected",
				})
				await (this as any).dispatch("integrations/fetchXeroStatus")
			} catch (error) {
				dispatch("handleError", { error }, { root: true })
			} finally {
				commit("SET_XERO_DISCONNECT_LOADING", false)
			}
		},
	},
}

export default integrationsStoreModule
